/* eslint-disable no-console */
import _ from 'lodash';

import {
  AUTH_USER,
  AUTH_ERROR,
  RESET_PASSWORD,
  CHANGE_PASSWORD,
  GET_USER_BY_OKTA_USER_ID,
  CLEAR_USER_PUSH_NOTIFICATIONS_FOR_DEVICE,
  SET_CURRENT_USER_ROLES,
  SET_CURRENT_USER_GROUPS,
} from '../constants/actions';
import {
  CHANGE_PASSWORD_ENDPOINT,
  GET_USER_BY_OKTA_USER_ID_ENDPOINT,
  RESET_PASSWORD_ENDPOINT,
  CLEAR_USER_PUSH_NOTIFICATIONS_FOR_DEVICE_ENDPOINT,
  MARK_OPERATOR_ACTIVE,
  RECORD_OPERATOR_LOGOUT,
} from '../constants/endpoints';
import { PUT, POST, GET, SUCCESS } from '../constants/general';
import { fetchOperators } from '../services/operator/handler';
import { apiCall, doRequest } from './api';
import { toggleAutoAssign } from './profile';
import { deauthUserAndUnsubscribeTopics } from './auth-utils';
import { serializeError } from '@app/utils/serialize-error';
import isLoadedFromMobile, { shouldUseNativeAuth } from '@app/helpers/platform';

const AUTO_ASSIGN_LOGOUT_TIMEOUT_MS = 10000;

let isRecordingLogout = false;
let heartbeatController = null;

/**
 * Prepares for logout by preventing new heartbeats and aborting any in-flight setOperatorActive request.
 */
export function prepareLogout() {
  isRecordingLogout = true;
  heartbeatController?.abort();
}

export function getIsRecordingLogout() {
  return isRecordingLogout;
}

export function registerHeartbeatController(controller) {
  heartbeatController = controller;
}

export function logout() {
  return async (dispatch, getState) => {
    prepareLogout();
    try {
      await doRequest({
        endpoint: RECORD_OPERATOR_LOGOUT,
        method: POST,
        headers: {
          'content-type': 'application/json',
          Accept: 'application/json',
        },
      });
    } catch (err) {
      console.error('Failed to record logout:', err);
    }

    if (isLoadedFromMobile() && !shouldUseNativeAuth()) {
      const args = {
        endpoint: CLEAR_USER_PUSH_NOTIFICATIONS_FOR_DEVICE_ENDPOINT,
        method: POST,
        action: CLEAR_USER_PUSH_NOTIFICATIONS_FOR_DEVICE,
        body: { deviceId: window?.deviceId },
        process: (t) => t,
      };
      dispatch(apiCall({ args }));
    }
    const isAutoAssign = _.get(getState(), 'auth.user.isAutoAssign');
    if (isAutoAssign) {
      let finished = false;
      const finishLogout = () => {
        if (finished) return;
        finished = true;
        clearTimeout(timeoutId);
        dispatch(deauthUserAndUnsubscribeTopics());
        isRecordingLogout = false;
      };

      const timeoutId = setTimeout(finishLogout, AUTO_ASSIGN_LOGOUT_TIMEOUT_MS);

      Promise.resolve(dispatch(toggleAutoAssign()))
        .then(finishLogout)
        .catch((err) => {
          console.error(err);
          finishLogout();
        });
    } else {
      dispatch(deauthUserAndUnsubscribeTopics());
      isRecordingLogout = false;
    }
  };
}

export function getUserByOktaUserId(oktaUserId) {
  const userId = oktaUserId?.toLowerCase();
  return (dispatch) => {
    const args = {
      endpoint: GET_USER_BY_OKTA_USER_ID_ENDPOINT(userId),
      method: GET,
      action: GET_USER_BY_OKTA_USER_ID,
      // Don't let a stale, overlapping bootstrap health check sign the user out:
      // if another attempt already reached SUCCESS by the time this one's UNAUTH
      // resolves, suppress the deauth. A genuine bootstrap 401 (not yet SUCCESS)
      // still deauths.
      skipDeauth: (getState) => getState()?.auth?.retrievedUserInfo === SUCCESS,
      process: ({ body: userInfo }) => {
        // The reducer will handle transforming organization to ownerOrganization
        dispatch({ type: AUTH_USER, payload: { user: userInfo } });
        return userInfo;
      },
    };
    dispatch(apiCall({ args }));
  };
}

export function setAuthError(error) {
  return { type: AUTH_ERROR, payload: serializeError(error) };
}

export function changePassword(data) {
  return (dispatch) => {
    try {
      const args = {
        endpoint: CHANGE_PASSWORD_ENDPOINT,
        method: PUT,
        action: CHANGE_PASSWORD,
        body: data,
      };
      dispatch(apiCall({ args }));
    } catch (e) {
      console.error(e);
    }
  };
}

export const resetPassword = (email) =>
  apiCall({
    args: {
      endpoint: RESET_PASSWORD_ENDPOINT,
      method: POST,
      action: RESET_PASSWORD,
      body: { email },
    },
  });

export function getCurrentUserRoles(userId) {
  return async (dispatch) => {
    if (!userId) {
      console.warn(
        'No user ID found in state, cannot fetch current user roles',
      );
      return;
    }

    try {
      // Use the actual fetchOperators function to get current user data
      const result = await fetchOperators({
        ids: [userId],
        limit: 1,
        includeGroupDetails: true, // needed for pendo init
        includeParentGroups: true,
      });

      // Always dispatch for consistency, using empty array as default when no roles found
      const userRoles = result?.operators[0]?.roles || [];
      const userGroups = result?.operators[0]?.groups || [];
      const userGroupLeads = result?.operators[0]?.groupLeads || [];
      const userParentGroups = result?.operators[0]?.parentGroups || [];
      dispatch({ type: SET_CURRENT_USER_ROLES, payload: { roles: userRoles } });
      dispatch({
        type: SET_CURRENT_USER_GROUPS,
        payload: {
          groups: userGroups,
          groupLeads: userGroupLeads,
          parentGroups: userParentGroups,
        },
      });
    } catch (error) {
      console.error(error);
    }
  };
}

/**
 * Marks the operator as active (heartbeat). Skips if logout is in progress to avoid racing with RECORD_OPERATOR_LOGOUT.
 * Supports AbortSignal so in-flight requests can be cancelled (e.g. on logout or before sending a newer heartbeat).
 *
 * @param {string} opID - Operator user ID.
 * @param {{ signal?: AbortSignal }} [opts] - Optional; pass { signal } from an AbortController to allow cancellation.
 * @returns {Promise<boolean>} - Whether a token refresh is required, or false on skip/error.
 */
export const setOperatorActive = async (opID, opts = {}) => {
  if (!opID) {
    return false;
  }
  if (isRecordingLogout) {
    return false;
  }

  try {
    const response = await doRequest({
      endpoint: MARK_OPERATOR_ACTIVE,
      method: 'PATCH',
      headers: {
        'x-marley-web-app-version': process.env.APP_VERSION,
        'content-type': 'application/json',
        Accept: 'application/json, application/xml, text/plain, text/html, *.*',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: 0,
      },
      body: { operatorID: opID },
      ...(opts.signal && { signal: opts.signal }),
    });

    const requireRefresh =
      response?.requireRefresh ?? response?.body?.requireRefresh;
    return requireRefresh;
  } catch (e) {
    // AbortError is expected when request is cancelled (e.g. logout or newer heartbeat)
    if (e?.name === 'AbortError') return false;
    console.error(e);
    return false;
  }
};
